import React, { memo, useCallback, useMemo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { EcwOrder, EcwOrdersResponse, getOrderIdDisplay } from '../EcwConstants';

interface EcwOrderInfoProps {
    ecwOrderList: EcwOrdersResponse;
    selectedOrderInfo: EcwOrder | null | undefined;
    handleRevertOrder: (orderId: string | number) => void;
}

// Static, so created once instead of on every render
const REVERT_TOOLTIP = <Tooltip id="tooltip-revert-order">Revert Order</Tooltip>;
const REVERT_DELAY = { show: 250, hide: 400 };

const getCodeField = (order: EcwOrder) => {
    switch (order.orderType) {
        case "VAC":
            return { label: "CVX", value: order.cvxCode };
        case "THINJ":
            return { label: "Medication Code", value: order.medicationAdministration?.medicationCode };
        default:
            return { label: "Rx Norm", value: order.medicationRequest?.rxNormCode };
    }
};

const EcwOrderInfo: React.FC<EcwOrderInfoProps> = ({ ecwOrderList, selectedOrderInfo, handleRevertOrder }) => {
    // Read localStorage once per mount instead of on every render
    const isSysAdmin = useMemo(
        () => localStorage.getItem("roleName")?.toLowerCase() === "sysadmin",
        []
    );
    const fallbackUser = useMemo(() => ({
        firstName: localStorage.getItem("firstName") ?? "",
        lastName: localStorage.getItem("lastName") ?? "",
    }), []);

    const orderId = selectedOrderInfo?.id || "";
    const onRevert = useCallback(() => handleRevertOrder(orderId), [handleRevertOrder, orderId]);

    if (!ecwOrderList || !selectedOrderInfo) return null;

    const {
        vaccineName, medicationRequest, medicationAdministration, provider, loggedUser,
        encounterId, encounterDate, facility, orderType, status, isSuccess,
        ecwResponseMessage, colour,
    } = selectedOrderInfo;

    const itemName = vaccineName || medicationRequest?.medicationName || medicationAdministration?.medicationName;
    const codeField = getCodeField(selectedOrderInfo);

    return (
        <div className='order-info-wrapper'>
            <div className='order-info-box'>
                <div>
                    <span>Patient</span>
                    <p><b>{ecwOrderList.lastName}, {ecwOrderList.firstName}</b> {ecwOrderList.gender} ({ecwOrderList.birthDate})</p>
                </div>
                <div>
                    <span>Item</span>
                    <p><b>{itemName}</b></p>
                </div>
                <div>
                    <span>Provider</span>
                    <p>{provider?.lastName} {provider?.firstName} ({provider?.npi})</p>
                </div>
                <div>
                    <span>User</span>
                    <p>{loggedUser?.lastName || fallbackUser.lastName}, {loggedUser?.firstName || fallbackUser.firstName}</p>
                </div>
                <div>
                    <span>Encounter</span>
                    <p>{encounterId} ({encounterDate})</p>
                </div>
                <div>
                    <span>Location</span>
                    <p>{facility?.facilityName} {orderType === "VAC" ? `(${facility?.facilityId})` : ""}</p>
                </div>
                <div>
                    <span>Order</span>
                    <p>{getOrderIdDisplay(selectedOrderInfo)} ({status})</p>
                </div>
                <div>
                    <span>{codeField.label}</span>
                    <p>{codeField.value}</p>
                </div>
            </div>
            <div className="order-status-box">
                <span>Processed</span>
                <i
                    className='status-square'
                    title={isSuccess === false ? (ecwResponseMessage ?? '') : ''}
                    style={{ backgroundColor: colour || 'gray' }}
                />
                {isSysAdmin && (
                    <OverlayTrigger placement="top" overlay={REVERT_TOOLTIP} delay={REVERT_DELAY}>
                        <i className="bi bi-arrow-counterclockwise revert-order-btn" onClick={onRevert} />
                    </OverlayTrigger>
                )}
            </div>
        </div>
    );
};

export default memo(EcwOrderInfo);